/**
 * Generated With: pnpm x packages/api/tools/generate-shopify-rest-model.ts
 */

import { compare } from "compare-versions";
import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyBillingAddressKey,
  ShopifyCheckoutAppliedGiftCardKey,
  ShopifyCheckoutAppliedGiftCardToCheckoutKey,
  ShopifyCheckoutKey,
  ShopifyCheckoutLineItemKey,
  ShopifyCheckoutLineItemToCheckoutKey,
  ShopifyCheckoutShippingRateKey,
  ShopifyCheckoutShippingRateToCheckoutKey,
  ShopifyCheckoutToBillingAddressKey,
  ShopifyCheckoutToCustomerKey,
  ShopifyCheckoutToLocationKey,
  ShopifyCheckoutToOrderKey,
  ShopifyCheckoutToShippingAddressKey,
  ShopifyCheckoutToShopKey,
  ShopifyCustomerKey,
  ShopifyLocationKey,
  ShopifyOrderKey,
  ShopifyShippingAddressKey,
  ShopifyShopKey,
} from "./keys";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyCheckoutKey,
  defaultFilterableApiIdentifiers: ["token"],
  "2022-01": {
    name: "Shopify Checkout",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id" } },
      },
      {
        name: "Abandoned Checkout URL",
        fieldType: FieldType.URL,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "abandoned_checkout_url" } },
      },
      {
        name: "Applied Discount",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "applied_discount" } },
      },
      {
        name: "Billing Address",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCheckoutToBillingAddressKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "billing_address" } },
        configuration: {
          relatedModelKey: ShopifyBillingAddressKey,
        },
      },
      {
        name: "Completed At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "completed_at" } },
      },
      {
        name: "Credit Card",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "credit_card" } },
      },
      {
        name: "Currency",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "currency" } },
      },
      {
        name: "Customer",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCheckoutToCustomerKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "customer_id" } },
        configuration: {
          relatedModelKey: ShopifyCustomerKey,
        },
      },
      {
        name: "Customer Locale",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "customer_locale" } },
      },
      {
        name: "Device",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "device_id" } },
      },
      {
        name: "Discount Code",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "discount_code" } },
      },
      {
        name: "Email",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "email" } },
      },
      {
        name: "Checkout Applied Gift Cards",
        fieldType: FieldType.HasMany,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "gift_cards" } },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyCheckoutAppliedGiftCardKey,
          inverseFieldKey: ShopifyCheckoutAppliedGiftCardToCheckoutKey,
        },
        isDeleted: (frameworkVersion) => compare(frameworkVersion, "^1.6.0", ">="),
      },
      {
        name: "Legal Notice URL",
        fieldType: FieldType.URL,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "legal_notice_url" } },
      },
      {
        name: "Line Items",
        fieldType: FieldType.HasMany,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "line_items" } },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyCheckoutLineItemKey,
          inverseFieldKey: ShopifyCheckoutLineItemToCheckoutKey,
        },
      },
      {
        name: "Location",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCheckoutToLocationKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "location_id" } },
        configuration: {
          relatedModelKey: ShopifyLocationKey,
        },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "name" } },
      },
      {
        name: "Note",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "note" } },
      },
      {
        name: "Note Attributes",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "note_attributes" } },
      },
      {
        name: "Buyer Accepts Marketing",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "buyer_accepts_marketing" } },
      },
      {
        name: "Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCheckoutToOrderKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "order" } },
        configuration: {
          relatedModelKey: ShopifyOrderKey,
        },
      },
      {
        name: "Order Status URL",
        fieldType: FieldType.URL,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "order_status_url" } },
      },
      {
        name: "Payment Due",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "payment_due" } },
      },
      {
        name: "Payment URL",
        fieldType: FieldType.URL,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "payment_url" } },
      },
      {
        name: "Payments",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "payments" } },
      },
      {
        name: "Phone",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "phone" } },
      },
      {
        name: "Presentment Currency",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "presentment_currency" } },
      },
      {
        name: "Privacy Policy URL",
        fieldType: FieldType.URL,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "privacy_policy_url" } },
      },
      {
        name: "Refund Policy URL",
        fieldType: FieldType.URL,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "refund_policy_url" } },
      },
      {
        name: "Requires Shipping",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "requires_shipping" } },
      },
      {
        name: "Reservation Time",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "reservation_time" } },
      },
      {
        name: "Reservation Time Left",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "reservation_time_left" } },
      },
      {
        name: "Shipping Address",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCheckoutToShippingAddressKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shipping_address" } },
        configuration: {
          relatedModelKey: ShopifyShippingAddressKey,
        },
      },
      {
        name: "Shipping Line",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shipping_line" } },
      },
      {
        name: "Shipping Policy URL",
        fieldType: FieldType.URL,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shipping_policy_url" } },
      },
      {
        name: "Shipping Rate",
        fieldType: FieldType.HasOne,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shipping_rate" } },
        configuration: {
          relatedModelKey: ShopifyCheckoutShippingRateKey,
          inverseFieldKey: ShopifyCheckoutShippingRateToCheckoutKey,
        },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "created_at" } },
      },
      {
        name: "Shopify Payments Account ID",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shopify_payments_account_id" } },
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "updated_at" } },
      },
      {
        name: "Source Identifier",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "source_identifier" } },
      },
      {
        name: "Source Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "source_name" } },
      },
      {
        name: "Source URL",
        fieldType: FieldType.URL,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "source_url" } },
      },
      {
        name: "Subscription Policy URL",
        fieldType: FieldType.URL,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "subscription_policy_url" } },
      },
      {
        name: "Subtotal Price",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "subtotal_price" } },
      },
      {
        name: "Tax Exempt",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "tax_exempt" } },
      },
      {
        name: "Tax Lines",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "tax_lines" } },
      },
      {
        name: "Tax Manipulations",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "tax_manipulations" } },
      },
      {
        name: "Taxes Included",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "taxes_included" } },
      },
      {
        name: "Terms Of Sale URL",
        fieldType: FieldType.URL,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "terms_of_sale_url" } },
      },
      {
        name: "Terms Of Service URL",
        fieldType: FieldType.URL,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "terms_of_service_url" } },
      },
      {
        name: "Token",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "token" } },
      },
      {
        name: "Total Line Items Price",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "total_line_items_price" } },
      },
      {
        name: "Total Price",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "total_price" } },
      },
      {
        name: "Total Tax",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "total_tax" } },
      },
      {
        name: "Total Tip Received",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "total_tip_received" } },
      },
      {
        name: "User ID",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "user_id" } },
      },
      {
        name: "Web URL",
        fieldType: FieldType.URL,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "web_url" } },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCheckoutToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: { type: "custom", resource: "checkout" }, // Special case, requires read_orders to list abandoned checkouts
  },
  "2024-01": {
    fields: [
      {
        name: "Discount Codes",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "discount_codes" } },
      },
      {
        name: "Discount Code",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "discount_code", deprecated: true } },
      },
    ],
  },
  "2025-04": {
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "id", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Email",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "email", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Device",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "device_id", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Discount Codes",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "discount_codes", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "created_at", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "updated_at", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Source Identifier",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "source_identifier", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Source Name",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "source_name", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Source URL",
        fieldType: FieldType.URL,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "source_url", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Subtotal Price",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "subtotal_price", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Tax Lines",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "tax_lines", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Taxes Included",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "taxes_included", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Token",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "token", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Total Line Items Price",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "total_line_items_price", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Total Price",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "total_price", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Total Tax",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "total_tax", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "User ID",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "user_id", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Shipping Line",
        fieldType: FieldType.JSON,
        isDeleted: true,
      },
      // on checkouts shipping lines come in with no id
      {
        name: "Shipping Lines",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "shipping_lines", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
        alias: ["Shipping Line"],
      },
      {
        name: "Shipping Address",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCheckoutToShippingAddressKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "shipping_address", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
        configuration: {
          relatedModelKey: ShopifyShippingAddressKey,
        },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "name", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Note",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "note", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Note Attributes",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "note_attributes", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Phone",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "phone", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Presentment Currency",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "presentment_currency", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Line Items",
        fieldType: FieldType.HasMany,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "line_items", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyCheckoutLineItemKey,
          inverseFieldKey: ShopifyCheckoutLineItemToCheckoutKey,
        },
      },
      {
        name: "Location",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCheckoutToLocationKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "location_id", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
        configuration: {
          relatedModelKey: ShopifyLocationKey,
        },
      },
      {
        name: "Customer",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCheckoutToCustomerKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "customer_id",
            availableInWebhook: { "checkouts/create": "customer.id", "checkouts/update": "customer.id" },
          },
        },
        configuration: {
          relatedModelKey: ShopifyCustomerKey,
        },
      },
      {
        name: "Customer Locale",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "customer_locale", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Currency",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "currency", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Completed At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "completed_at", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
      {
        name: "Buyer Accepts Marketing",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "buyer_accepts_marketing",
            availableInWebhook: ["checkouts/create", "checkouts/update"],
          },
        },
      },
      {
        name: "Billing Address",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCheckoutToBillingAddressKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "billing_address", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
        configuration: {
          relatedModelKey: ShopifyBillingAddressKey,
        },
      },
      {
        name: "Abandoned Checkout URL",
        fieldType: FieldType.URL,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "abandoned_checkout_url", availableInWebhook: ["checkouts/create", "checkouts/update"] },
        },
      },
    ],
    syncConfig: {
      type: "webhook_only",
    },
  },
};

export const ShopifyCheckout = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
